import GuiContext from "config/context/guiContext";
import { useCallback, useContext, useState } from "react";
import { useMfImport } from "../context/MfImportContext";
import { useGetDatabaseStructureService } from './useGetDatabaseStructureService';
import { useUpdatePermissions } from "api/permissions/usePermissions";
import { useInvalidateMemory } from "features/memory";
import { toWizardField } from 'features/compositions/utils/fieldTypes';
import { posaljiSaRuckom } from './fileHandles';


const initialServerStatus = {
    response: null,
    loading: false,
    error: null,
}

/**
 * Ekstenzija fajla → slug plugin-a, za izvore koji se otpremaju kao fajl.
 *
 * Izvori sa konekcijom (MySQL, Mongo) nemaju fajl ni ekstenziju — njihov slug
 * korisnik bira iz kataloga, pa im ovde nije mesto.
 */
const SLUG_BY_EXTENSION = {
    csv: 'csv-import',
    json: 'json-import',
    xml: 'xml-import',
};

export const slugForFile = (file) =>
    SLUG_BY_EXTENSION[String(file?.name || '').split('.').pop().toLowerCase()];

/**
 * Jedan korak uvoza. Telo je uvek JSON: fajl, kad ga ima, putuje kao ručka —
 * otpremljen jednom, pre prvog koraka (`fileHandles.js`).
 *
 * `config` ide u zasebnom objektu, a ne razliveno po telu: `database` u
 * kontekstu importa je IneryDB nalog, a u MySQL konfiguraciji ime izvorne
 * baze — ravno telo bi to spojilo i uvezlo pogrešnu tabelu bez greške.
 *
 * @returns {Promise<object>} JSON odgovor
 */
const posalji = async ({ URL, server, slug, file, config, fields, ruta, trosi = false }) => {
    const polja = { ...fields, ...(config && Object.keys(config).length ? { config } : {}) };

    if (file) return posaljiSaRuckom({ URL, server, slug, file, ruta, polja, trosi });

    const res = await fetch(`${URL}/api/imports/${slug}${ruta}`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(polja),
    });
    return res.json();
};

/**
 * Struktura sa servera → oblik koji čarobnjak koristi.
 *
 * `/structure` vraća `fields` i `indexes` razdvojeno, jer tako izgleda
 * kompozicija na lancu. Čarobnjak radi sa jednim nizom u kome svako polje nosi
 * `is_index`, jer korisnik u koraku 2 čekira šta postaje indeks.
 *
 * Indeksi idu prvi da bi automatski primarni ključ, koji host uvek generiše,
 * bio vidljiv na vrhu — ranije ga korisnik nije video uopšte.
 */
const toWizardTable = (structure = {}) => ({
    ...structure,
    fields: [
        ...(structure.indexes || []).map((f) => ({ ...toWizardField(f), is_index: true })),
        ...(structure.fields || []).map((f) => ({ ...toWizardField(f), is_index: false })),
    ],
});

/**
 * Ime cilja → ime kompozicije. MORA da se poklapa sa `compositionNameFor` u
 * `blockchain/plugins/host/runImport.js`, jer host po tom imenu pravi
 * kompoziciju, a čarobnjak ga korisniku prikazuje pre nego što to uradi.
 *
 * MySQL tabela i Mongo kolekcija prolaze neizmenjene. Redis obrazac ne može:
 * `patient:*` nosi glob znakove koji u imenu kompozicije nemaju šta da traže.
 *
 * Od golog obrasca (`*`, `?`) ne ostane ništa, pa se ime PREDLAŽE umesto da se
 * vrati sam obrazac — kompozicija se inače zvala `*`, što na spisku izgleda kao
 * džoker a ne kao tabela. Vidi isti komentar uz host.
 */
const SVI_KLJUCEVI = 'all';

export const compositionNameFor = (target) => {
    const cleaned = String(target)
        .replace(/[*?[\]]/g, '')
        .replace(/[:./\\-]+$/g, '')
        .replace(/[:./\\-]+/g, '_')
        .trim();
    return cleaned || SVI_KLJUCEVI;
};

export const useParseMf = () => {
    const [response, setResponse] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const {
        setTable, setTables, setDegradations, setSelectedComposition,
        setDerivedName, allCompositionsList, setExisting,
    } = useMfImport();

    const {
        selected: { selectedServer, selectedAccount },
        userData,
        URL
    } = useContext(GuiContext);

    /**
     * @param {{slug: string, file?: File, config?: object}} args
     *   `file` za izvore koji se otpremaju, `config` za one sa konekcijom.
     */
    const parseMf = async ({ slug, file, config }) => {

        if (!slug) {
            setError('No import source selected');
            return;
        }
        if (!file && !config) {
            setError('Missing required fields');
            return;
        }

        const fields = {
            server_url: selectedServer || "",
            // Nove rute su iza requirePlugin — bez ovoga vraćaju 403, a
            // korisnik vidi „plugin nije instaliran" umesto strukture.
            guiname: userData?.guiname || "",
            account_id: userData?.id || "",
            // Nalog u koji se uvozi — da bi server rekao šta je od ciljeva već
            // na lancu, pre nego što korisnik izabere ime.
            database: selectedAccount || "",
        };

        setLoading(true);

        try {
            const data = await posalji({ URL, server: selectedServer, slug, file, config, fields, ruta: '/structure' });
            setResponse(data);

            if (data?.status) {
                // Više izabranih celina (tabela, kolekcija, obrazaca) → server
                // vraća `structures`, mapu ime cilja → šema. Jedna celina i
                // dalje vraća `structure`, pa se stari tok ne menja.
                if (data.structures) {
                    const list = Object.entries(data.structures).map(([target, structure]) => ({
                        target,
                        name: compositionNameFor(target),
                        ...toWizardTable(structure),
                    }));
                    setTables(list);
                    setTable(list[0] || { fields: [], indexes: [], description: '' });
                    // Ime prve, da korak 2 ima šta da prikaže i pre prvog klika.
                    setSelectedComposition(list[0]?.name || '');
                } else {
                    setTables([]);
                    setTable(toWizardTable(data.structure));
                    // Jedan cilj dobija isto izvedeno ime koje više njih već
                    // dobija — tabela je poznata, pa nema razloga da se ime
                    // prekucava. Kad je takvo ime u bazi zauzeto, predlog
                    // IZOSTAJE: uvoz ne može da menja postojeću kompoziciju, pa
                    // bi ponuđeno ime vodilo pravo u odbijanje. Tada korisnik
                    // unosi svoje, a poruka uz polje kaže zašto.
                    //
                    // Upisuje se SAMO u `derivedName`, ne i u `selectedComposition`:
                    // na jezičku „Use existing" to isto polje znači izabranu
                    // postojeću kompoziciju, pa bi predlog tamo izgledao kao izbor
                    // koji korisnik nije napravio. Preuzima ga `MfAddComposition`
                    // kad se otvori jezičak kome pripada.
                    const suggested = data.target ? compositionNameFor(data.target) : '';
                    const free = Boolean(suggested) && !allCompositionsList.has(suggested.toLowerCase());
                    setDerivedName(free ? suggested : '');
                }
                // Šta se pri prelasku gubi — prazno za izvore koji nemaju
                // konstrukte bez ekvivalenta. Stare rute ovo nisu ni vraćale.
                setDegradations(data.degradations || []);
                setExisting(data.existing || []);
            } else {
                setError(data?.message);
            }
        } catch (err) {
            setError(err.message || 'An error occurred');
        } finally {
            setLoading(false);
        }
    };


    const resetServerStatus = useCallback(() => {
        setLoading(initialServerStatus.loading);
        setResponse(initialServerStatus.response);
        setError(initialServerStatus.error);
    }, [])

    return {
        response,
        loading,
        error,
        parseMf,
        resetServerStatus
    };
};

export const useUploadMf = () => {
    const [response, setResponse] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const { getDatabaseStructure } = useGetDatabaseStructureService();
    const { updatePermissions } = useUpdatePermissions();
    const invalidateMemory = useInvalidateMemory();

    const {
        selected: { selectedAccount, selectedServer },
        userData,
        URL
    } = useContext(GuiContext);

    /**
     * @param {{slug: string, source: string, data: object}} args
     *   `data.file` za izvore koji se otpremaju, `data.config` za konekcione.
     */
    const uploadMf = async ({ slug, source, data }) => {

        if (!slug) {
            setError('No import source selected');
            return;
        }

        // Jedna kompozicija: ime bira korisnik, strukturu je odobrio u koraku
        // pre — pa se šalju odvojeno.
        const composition = data.compositions?.[0] || {};

        // Više kompozicija: imena dolaze IZ ciljeva, a šeme se šalju kao mapa
        // ime cilja → šema. Host tada deploy-uje JEDAN kontrakt sa svim
        // kompozicijama, pa streamuje podatke jednu po jednu; N zasebnih poziva
        // bi ceo kontrakt poslalo N puta.
        const many = (data.compositions || []).length > 1;
        const structures = many
            ? data.compositions.reduce((acc, c) => {
                acc[c.target || c.name] = {
                    description: c.description || `Imported from ${source || slug}`,
                    fields: c.fields || [],
                    indexes: c.indexes || [],
                };
                return acc;
            }, {})
            : undefined;

        // Prepravljanje fajla u pregledaču ima smisla samo za fajl izvore, i
        // samo dok stara ruta ne ume da popuni izostalu vrednost sama.
        const file = data.file
            ? await normalizeMfFileForNowDefaults(data.file, data.compositions)
            : undefined;

        const zahtev = {
            URL,
            server: selectedServer,
            slug,
            file,
            config: data.config,
            ruta: '',
            trosi: true,
            fields: {
                server_url: selectedServer || "",
                database: selectedAccount || "",
                guiname: userData?.guiname || "",
                account_id: userData?.id || "",
                // Kod više kompozicija imena dolaze iz ciljeva, pa se jedno ime
                // ne šalje — host bi ga ignorisao, a u telu bi zbunjivalo.
                composition: many ? "" : (composition.name || ""),
                permission: data.permission || "",
                private_key: data.private_key || "",
                exist: data.exist,
                // Potvrda da se piše u kompoziciju koja već ima redove; bez nje
                // server odgovara 409 i ništa ne upisuje.
                append: data.append === true,
                scope: data.scope,
                // Struktura koju je korisnik video i doradio u fazi 1. Bez nje
                // bi host zaključio iznova iz podataka i odbacio svaki izbor
                // indeksa koji je korisnik napravio.
                ...(many
                    ? { structures }
                    : {
                        structure: {
                            description: composition.description || `Imported from ${source || slug}`,
                            fields: composition.fields || [],
                            indexes: composition.indexes || [],
                        },
                    }),
            },
        };

        setLoading(true);

        try {
            const data = await posalji(zahtev);
            setResponse(data);
            getDatabaseStructure();
            updatePermissions();
            // Uvoz menja zauzeće na lancu najviše od svega — izmereno
            // 06.10.2026. na jednoj kolekciji od 4 dokumenta: 3 140 -> 631 700.
            // Bez ovoga widget pokazuje staro zauzeće do `F5`.
            invalidateMemory();

            if (!data?.status) {
                setError(data?.message);
            }
        } catch (err) {
            setError(err.message || 'An error occurred');
        } finally {
            setLoading(false);
        }
    };


    const resetServerStatus = useCallback(() => {
        setLoading(initialServerStatus.loading);
        setResponse(initialServerStatus.response);
        setError(initialServerStatus.error);
    }, [])

    return {
        response,
        loading,
        error,
        uploadMf,
        resetServerStatus
    };
};

/**
 * Polja sa podrazumevanom vrednošću NOW() se prazne, da bi ih kontrakt popunio.
 *
 * Tip se sada izvodi iz imena fajla, a ne prima spolja — pozivalac više ne barata
 * ekstenzijom, nego slug-om plugin-a.
 */
const normalizeMfFileForNowDefaults = async (file, compositions = []) => {
    if (!file) return file;

    const nowDefaultNames = getNowDefaultTimePointNames(compositions?.[0]);
    if (!nowDefaultNames.size) return file;

    const type = String(file.name || "").split(".").pop().toLowerCase();

    if (type === "json") {
        return normalizeJsonMfFile(file, nowDefaultNames);
    }

    if (type === "csv") {
        return normalizeCsvMfFile(file, nowDefaultNames);
    }

    return file;
};

const getNowDefaultTimePointNames = (composition = {}) => {
    const fields = composition?.fields || [];
    const indexes = composition?.indexes || [];
    return new Set(
        [...fields, ...indexes]
            .filter((entry) => entry?.type === "time_point" && String(entry?.default_value || "").trim().toUpperCase() === "NOW()")
            .map((entry) => entry.name),
    );
};

const normalizeJsonMfFile = async (file, nowDefaultNames) => {
    const text = await file.text();
    let parsed;
    try {
        parsed = JSON.parse(text);
    } catch (e) {
        return file;
    }

    const rows = Array.isArray(parsed) ? parsed : [parsed];
    const normalizedRows = rows.map((row = {}) => {
        const normalized = { ...row };
        nowDefaultNames.forEach((name) => {
            if (normalized[name] === undefined || normalized[name] === "") {
                normalized[name] = null;
            }
        });
        return normalized;
    });

    const content = JSON.stringify(Array.isArray(parsed) ? normalizedRows : normalizedRows[0]);
    return new File([content], file.name, { type: file.type || "application/json" });
};

const normalizeCsvMfFile = async (file, nowDefaultNames) => {
    const text = await file.text();
    const lines = text.replace(/\r/g, "").split("\n");
    if (lines.length < 2) return file;

    const headers = parseCsvRow(lines[0]);
    const targetIndexes = headers
        .map((header, index) => (nowDefaultNames.has(header) ? index : -1))
        .filter((index) => index !== -1);

    if (!targetIndexes.length) return file;

    const normalizedLines = [lines[0]];
    for (let i = 1; i < lines.length; i++) {
        if (!lines[i].trim()) {
            normalizedLines.push(lines[i]);
            continue;
        }

        const row = parseCsvRow(lines[i]);
        targetIndexes.forEach((index) => {
            if (row[index] === undefined || row[index] === "") {
                row[index] = "null";
            }
        });
        normalizedLines.push(toCsvRow(row));
    }

    return new File([normalizedLines.join("\n")], file.name, { type: file.type || "text/csv" });
};

const parseCsvRow = (row) => {
    const result = [];
    let currentField = "";
    let insideQuotes = false;

    for (const char of row) {
        if (char === '"') {
            insideQuotes = !insideQuotes;
        } else if (char === "," && !insideQuotes) {
            result.push(currentField);
            currentField = "";
        } else {
            currentField += char;
        }
    }

    result.push(currentField);
    return result;
};

const toCsvRow = (fields = []) =>
    fields
        .map((value) => {
            const str = String(value ?? "");
            if (str.includes(",") || str.includes('"') || str.includes("\n")) {
                return `"${str.replace(/"/g, '""')}"`;
            }
            return str;
        })
        .join(",");